<template>
  <div class="form-group">
    <label :for="name">{{ t(`labels.${props.label ?? props.name}`) }}</label>

    <!-- InputGroup when field accepts placeholder data OR has placeholder -->
    <div
      v-if="acceptsPlaceholderData || !!props.placeholder"
      class="p-inputgroup"
      :class="[
        {
          'dirty-state-wrapper': isFieldDirty && !disabled,
          'error-state-wrapper': showError && !disabled,
        },
      ]"
    >
      <PlaceholderSourceButton
        :placeholderSource="props.placeholderSource"
        :placeholder="formattedPlaceholder"
        :currentValue="internalValue"
        :isVisible="isPlaceholderDifferent"
        :disabled="disabled"
        @accept="handleAcceptPlaceholder"
      />
      <div :class="['form-input-number-container', alignmentClass]">
        <InputNumber
          :id="name"
          v-model="internalValue"
          class="w-full"
          :inputId="inputId"
          :placeholder="formattedPlaceholder"
          :minFractionDigits="minFractionDigits"
          :maxFractionDigits="maxFractionDigits"
          :mode="formatMode"
          :currency="currency"
          :prefix="prefix"
          :suffix="suffix"
          :locale="currentLocale"
          :useGrouping="effectiveUseGrouping"
          :disabled="disabled"
          :class="{
            'dirty-state-wrapper': isFieldDirty,
          }"
          :min="minValue"
          :max="maxValue"
          @input="handleInput"
          @dblclick="onPlaceholderDblClick"
        />
      </div>
    </div>

    <!-- Plain InputNumber (if no InputGroup needed) -->
    <div :class="['form-input-number-container', alignmentClass]">
      <InputNumber
        v-if="!acceptsPlaceholderData && !props.placeholder"
        :id="name"
        v-model="internalValue"
        class="w-full"
        :inputId="inputId"
        :placeholder="formattedPlaceholder"
        :minFractionDigits="minFractionDigits"
        :maxFractionDigits="maxFractionDigits"
        :mode="formatMode"
        :currency="currency"
        :prefix="prefix"
        :suffix="suffix"
        :locale="currentLocale"
        :useGrouping="effectiveUseGrouping"
        :disabled="disabled"
        :class="{
          'dirty-state-wrapper': isFieldDirty,
          'error-state-wrapper': showError,
        }"
        :min="minValue"
        :max="maxValue"
        @input="handleInput"
        @dblclick="onPlaceholderDblClick"
      />
    </div>

    <FormError v-if="showError" :message="errorMessage" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, unref, watch } from 'vue';
import type { InputNumberInputEvent } from 'primevue/inputnumber';
import InputNumber from 'primevue/inputnumber';
import { useField, useForm } from 'vee-validate';
import { useI18n } from 'vue-i18n';

import type { InvoiceItemSuggestionSource } from '@/views/operatingcostinvoices/components/operationcostinvoicelines/operatingCostInvoiceTypes';
import { translatePrefixedOrValue } from '@/utils/i18n';
import { safeStringify } from '@/utils/string';

import PlaceholderSourceButton from './elements/PlaceholderSourceButton.vue';
import FormError from './errors/FormError.vue';

const props = withDefaults(
  defineProps<{
    name: string;
    modelValue: number | undefined;
    initialValue: number | undefined;
    placeholder?: number | string;
    isDirty: (dirty: boolean) => void;
    label?: string;
    disabled?: boolean;
    inputId?: string;
    useGrouping?: boolean;
    formatType?: 'decimal' | 'currency' | 'squaremeter';
    currency?: string;
    decimals?: number;
    locale?: string;
    minValue?: number;
    maxValue?: number;
    prefix?: string;
    suffix?: string;
    placeholderSource?: InvoiceItemSuggestionSource;
    onAcceptPlaceholder?: () => void;
    acceptsPlaceholderData?: boolean;
    acceptPlaceholderOnDblClick?: boolean;
    alignRight?: boolean;
  }>(),
  {
    placeholder: undefined,
    label: undefined,
    inputId: undefined,
    formatType: undefined,
    currency: undefined,
    decimals: undefined,
    locale: undefined,
    minValue: undefined,
    maxValue: undefined,
    prefix: undefined,
    suffix: undefined,
    placeholderSource: undefined,
    onAcceptPlaceholder: undefined,
    alignRight: true,
  },
);

const i18n = useI18n();
const { t, locale: i18nLocale } = i18n;

const { errorMessage, meta } = useField<number>(props.name);
const { submitCount } = useForm();
const showError = computed(() => {
  if (props.disabled) return false;
  if (!errorMessage.value) return false;
  return meta.touched || submitCount.value > 0;
});

const internalValue = ref(props.modelValue);
const initialValue = computed(() => props.initialValue);
const isFieldDirty = ref(false);
const inputId = computed(() => props.inputId || 'locale-user');

const formatMode = computed<'decimal' | 'currency'>(() => {
  // PrimeVue InputNumber supports only decimal|currency modes.
  // Our custom squaremeter format is a decimal number with a suffix.
  if (props.formatType === 'currency') {
    return 'currency';
  }
  return 'decimal';
});
const currency = computed(() => {
  // Always default to EUR if not provided, to prevent Intl.NumberFormat crashes
  return props.currency || 'EUR';
});

const currentLocale = computed(() => {
  // If explicit locale provided, use it
  if (props.locale) {
    return props.locale;
  }

  // Otherwise, derive from i18n
  const localeValue = unref(i18nLocale);
  switch (localeValue) {
    case 'de':
      return 'de-DE';
    case 'fr':
      return 'fr-FR';
    case 'hu':
      return 'hu-HU';
    case 'en':
    default:
      return 'en-GB';
  }
});

const decimals = computed(() => {
  // If decimals explicitly provided, use it
  if (props.decimals !== undefined) {
    return props.decimals;
  }
  // Default: 2 decimals for currency, 0 for decimal
  return formatMode.value === 'currency' ? 2 : 0;
});

const effectiveUseGrouping = computed(() => {
  // Smart default: Currency MUST have grouping (thousands separator)
  // This is the financial standard and should not be overridden by accident
  if (formatMode.value === 'currency') {
    return true; // Currency ALWAYS uses grouping/thousands separators
  }

  // For decimal mode, if explicitly provided, use that
  if (props.useGrouping !== undefined) {
    return props.useGrouping;
  }

  // For decimal mode, default to true (PrimeVue standard behavior)
  return true;
});

const minFractionDigits = computed(() => decimals.value);
const maxFractionDigits = computed(() => decimals.value);

/**
 * Compute placeholder with optional formatting
 */
const formattedPlaceholder = computed(() => {
  if (props.placeholder === undefined || props.placeholder === null) return '';

  if (typeof props.placeholder === 'string') {
    const translated = translatePrefixedOrValue(i18n, 'placeholder', props.placeholder);

    // Numeric placeholders are rendered with the same formatting as actual values
    // so currency/decimal fields stay visually consistent.
    const n = toNumber(props.placeholder);
    if (Number.isFinite(n)) return formatForDisplay(n);

    const translatedNumber = toNumber(translated);
    if (Number.isFinite(translatedNumber)) return formatForDisplay(translatedNumber);

    return translated;
  }

  const n = toNumber(props.placeholder);
  if (!Number.isFinite(n)) return String(props.placeholder);

  // Format using Intl like the actual value
  return formatForDisplay(n);
});

/**
 * Determines if button should be visible
 * Compares current value with placeholder
 */
const isPlaceholderDifferent = computed(() => {
  if (
    !props.placeholderSource ||
    props.placeholder === undefined ||
    props.placeholder === null ||
    props.disabled
  ) {
    return false;
  }

  const currentNum = toNumber(internalValue.value);
  const placeholderNum = toNumber(props.placeholder);

  // Consider different if: one is null/NaN and the other isn't, or they're numerically different
  if (!Number.isFinite(currentNum) && !Number.isFinite(placeholderNum)) return false;
  if (!Number.isFinite(currentNum) || !Number.isFinite(placeholderNum)) return true;
  return currentNum !== placeholderNum;
});

const acceptsPlaceholderData = computed(() => props.acceptsPlaceholderData ?? false);

const alignmentClass = computed(() => {
  if (props.alignRight === true) return 'align-right';
  if (props.alignRight === false) return 'align-left';
  if (props.alignRight === 'center') return 'align-center';
  return 'align-right'; // default
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: number | null | undefined): void;
}>();

const handleInput = (event: InputNumberInputEvent) => {
  const value = typeof event.value === 'number' || event.value === undefined ? event.value : null;
  emit('update:modelValue', value);
};

/**
 * Helper: Convert value to number safely
 */
function toNumber(val: unknown): number {
  if (typeof val === 'number') return Number.isFinite(val) ? val : NaN;

  const raw = (safeStringify(val) ?? '').trim();
  if (raw === '') return NaN;

  // Keep only characters relevant for numeric parsing.
  const cleaned = raw.replace(/[^\d,.-]/g, '');
  if (cleaned === '') return NaN;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');

  let normalized = cleaned;

  // If both separators exist, treat the right-most one as decimal separator.
  if (lastComma !== -1 && lastDot !== -1) {
    const decimalSeparator = lastComma > lastDot ? ',' : '.';
    const groupSeparator = decimalSeparator === ',' ? '.' : ',';
    normalized = normalized.split(groupSeparator).join('');
    if (decimalSeparator === ',') {
      normalized = normalized.replace(',', '.');
    }
  } else if (lastComma !== -1) {
    // Comma-only values are interpreted as decimal comma.
    normalized = normalized.replace(',', '.');
  }

  const n = Number(normalized);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Helper: Format number for display
 */
function formatForDisplay(val: unknown): string {
  const n = toNumber(val);
  if (!Number.isFinite(n)) return '';

  // Determine style: use currency only if we have a valid currency code
  const shouldUseCurrency = formatMode.value === 'currency' && !!currency.value;
  const style = shouldUseCurrency ? 'currency' : 'decimal';

  try {
    const nf = new Intl.NumberFormat(currentLocale.value, {
      style,
      ...(shouldUseCurrency && { currency: currency.value }),
      minimumFractionDigits: minFractionDigits.value,
      maximumFractionDigits: maxFractionDigits.value,
    });

    return nf.format(n);
  } catch (_error) {
    // Fallback: if anything goes wrong with formatting, just return raw number
    return String(n);
  }
}

/**
 * Accept placeholder from button click
 */
function handleAcceptPlaceholder() {
  if (
    props.disabled ||
    !props.placeholderSource ||
    props.placeholder === undefined ||
    props.placeholder === null
  ) {
    return;
  }

  const placeholderNum = toNumber(props.placeholder);
  internalValue.value = placeholderNum;
  emit('update:modelValue', placeholderNum);

  props.onAcceptPlaceholder?.();
}

/**
 * Toggle placeholder on double-click:
 * - If current value == placeholder → delete it (toggle OFF)
 * - If field is empty → accept placeholder (toggle ON)
 * - Otherwise → do nothing
 */
function onPlaceholderDblClick() {
  if (props.disabled) return;
  if (!props.acceptPlaceholderOnDblClick) return;
  if (!props.placeholderSource || props.placeholder === undefined || props.placeholder === null) {
    return;
  }

  const currentNum = toNumber(internalValue.value);
  const placeholderNum = toNumber(props.placeholder);

  const isEmpty = !Number.isFinite(currentNum);
  const placeholderValid = Number.isFinite(placeholderNum);

  // --- CASE A: Toggle OFF (value == placeholder → delete) ---
  if (placeholderValid && currentNum === placeholderNum) {
    internalValue.value = undefined;
    emit('update:modelValue', undefined);
    return;
  }

  // --- CASE B: Toggle ON (only if field is empty) ---
  if (!isEmpty) return;

  // Adopt placeholder as actual value
  internalValue.value = placeholderNum;
  emit('update:modelValue', placeholderNum);
}

watch([internalValue, initialValue], () => {
  isFieldDirty.value = internalValue.value !== initialValue.value;
  props.isDirty(isFieldDirty.value);
});

watch(
  () => props.modelValue,
  (newValue) => {
    internalValue.value = newValue;
  },
);
</script>

<style scoped>
.form-group {
  margin-bottom: 1rem;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

:deep(.form-input-number-container) {
  flex: 1;
}

/* Alignment control via container classes */
:deep(.form-input-number-container.align-right .p-inputnumber-input) {
  text-align: right !important;
}

:deep(.form-input-number-container.align-center .p-inputnumber-input) {
  text-align: center !important;
}

:deep(.placeholder-source-btn + .form-input-number-container .p-inputnumber-input) {
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
}

:deep(.form-input-number-container .p-inputnumber-input.my-2) {
  margin-top: 0 !important;
}
</style>
